import { GenericSearchSelect, type ApiFetchFunction } from '@app/components/ui/GenericSearchSelect';
import { ClientDisplay } from '@feat-scheduling/components/VisitCalendarEvent/ClientDisplay';
import { Button, Segmented } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BreachFilterOption, BreachGroupBy, BreachGroupPrefs } from './breachGrouping';
import { collapseAll, expandAll, switchGroupBy } from './breachGroupPrefs';

type GroupOption = BreachFilterOption;

export type BreachGroupControlsProps = {
  prefs: BreachGroupPrefs;
  onPrefsChange: (next: BreachGroupPrefs) => void;
  /** The groups this window's data actually has, from `breachFilterOptions`. */
  filterOptions: { label: string; value: string }[];
  disabled?: boolean;
};

/**
 * Group-by and filter for the out-of-range list, shaped like the shifts grid's
 * `ShiftsGroupControls` — same Segmented-plus-multi-select, so the two screens
 * are learned once.
 */
export const BreachGroupControls = ({
  prefs,
  onPrefsChange,
  filterOptions,
  disabled = false,
}: BreachGroupControlsProps) => {
  const { t } = useTranslation();

  /**
   * The picker searches and pages the groups THIS WINDOW's data has, in memory.
   *
   * There is no endpoint to ask "which patients breached", so there is nothing to
   * fetch — the same reason `ClientSelectGeneric.restrictTo` serves locally
   * instead of querying. Paging a list this short is not the point; being the
   * same control the rest of the product uses is.
   */
  const fetchGroups = useCallback<ApiFetchFunction<GroupOption>>(
    async ({ limit, offset, query }) => {
      const term = (query ?? '').trim().toLocaleLowerCase();
      const matches = term
        ? filterOptions.filter((option) => option.label.toLocaleLowerCase().includes(term))
        : filterOptions;
      return { data: { data: matches.slice(offset, offset + limit), total: matches.length, offset } };
    },
    [filterOptions]
  );

  // A selected id whose group has left the window keeps its id but has no label
  // to render; falling back to the id beats dropping the chip, which would read
  // as "that filter is off" while it is still applied.
  const selectedGroups = useCallback(
    (): GroupOption[] =>
      prefs.selectedGroupIds.map(
        (value) => filterOptions.find((option) => option.value === value) ?? { value, label: value }
      ),
    [prefs.selectedGroupIds, filterOptions]
  );

  /**
   * The groups the screen is actually showing — what "all" has to mean.
   *
   * Not every group in the window: with a filter applied, the hidden ones are
   * not on screen to be expanded, and marking them open would unfold the list
   * behind the doctor's back the moment the filter came off.
   */
  const visibleGroupIds = useMemo(() => {
    const present = filterOptions.map((option) => option.value);
    const selected = prefs.selectedGroupIds.filter((id) => present.includes(id));
    return selected.length > 0 ? selected : present;
  }, [filterOptions, prefs.selectedGroupIds]);

  // Partial counts as "not all open", so the button offers to finish the job.
  const allOpen = visibleGroupIds.length > 0 && visibleGroupIds.every((id) => prefs.expandedGroupIds.includes(id));

  const groupByOptions = useMemo(
    () => [
      { label: t('doctor-breaches-group-by-patient'), value: 'patient' as BreachGroupBy },
      { label: t('doctor-breaches-group-by-metric'), value: 'metric' as BreachGroupBy },
      { label: t('doctor-breaches-group-by-visit'), value: 'visit' as BreachGroupBy },
      { label: t('doctor-breaches-group-by-none'), value: 'none' as BreachGroupBy },
    ],
    [t]
  );

  return (
    <div className="flex items-center gap-2 flex-wrap min-w-0">
      <Segmented<BreachGroupBy>
        size="small"
        aria-label={t('doctor-breaches-group-by-label')}
        options={groupByOptions}
        disabled={disabled}
        value={prefs.groupBy}
        // Through `switchGroupBy`, which clears a selection whose ids named the
        // dimension being left behind.
        onChange={(groupBy) => onPrefsChange(switchGroupBy(prefs, groupBy))}
      />
      {prefs.groupBy !== 'none' && (
        // The box, not the control, carries the width: SearchSelectV2 hardcodes
        // `width: '100%'` inline, which beats any class this could be given, so
        // sizing it from the outside is the only thing that holds.
        <div className="w-[300px] shrink-0">
          <GenericSearchSelect<GroupOption>
            mode="multiple"
            size="small"
            allowClear
            maxTagCount="responsive"
            aria-label={t('doctor-breaches-group-filter-placeholder')}
            placeholder={t('doctor-breaches-group-filter-placeholder')}
            disabled={disabled}
            apiFetch={fetchGroups}
            getItemId={(option) => option.value}
            getItemLabel={(option) => option.label}
            selectedItemsProvider={selectedGroups}
            // A person reads as a person here too — the same avatar-and-name the
            // patient list and the calendar use. A visit gets one as well, since
            // a visit is somebody's visit, with its date underneath to tell one
            // of that patient's visits from the next. Only a metric has no face.
            renderOption={(option) =>
              option.person ? (
                <div className="flex flex-col">
                  <ClientDisplay firstName={option.person.firstName} lastName={option.person.lastName} />
                  {/* Indented past the avatar so it reads as that person's date. */}
                  {option.subtitle && <span className="text-[11px] text-slate-400 ml-8">{option.subtitle}</span>}
                </div>
              ) : (
                <span>{option.label}</span>
              )
            }
            // Switching group-by replaces the whole option set — patients for
            // metrics — so the picker has to reload rather than search a stale list.
            reloadSignal={`${prefs.groupBy}:${filterOptions.length}`}
            // Clearing hands back undefined, which must read as "no filter", not
            // "hide everything".
            onSelectionChange={(selection) => {
              const chosen = selection === undefined ? [] : Array.isArray(selection) ? selection : [selection];
              onPrefsChange({ ...prefs, selectedGroupIds: chosen.map((option) => option.value) });
            }}
          />
        </div>
      )}
      {prefs.groupBy !== 'none' && (
        // One button, not two: with the state named on it there is only ever one
        // useful action, and a disabled "collapse all" on an already-folded
        // screen is furniture.
        <Button
          size="small"
          type="link"
          className="!px-0"
          disabled={disabled || visibleGroupIds.length === 0}
          onClick={() => onPrefsChange(allOpen ? collapseAll(prefs) : expandAll(prefs, visibleGroupIds))}
        >
          {t(allOpen ? 'doctor-breaches-collapse-all' : 'doctor-breaches-expand-all')}
        </Button>
      )}
    </div>
  );
};

export default BreachGroupControls;
